import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  normalizeRichTextHtml,
  sanitizeRichTextHtml,
  sectionedTextToRichTextHtml,
} from 'src/app/shared/utils/rich-text-content.utils';

@Component({
  selector: 'app-wysiwyg-lite',
  templateUrl: './wysiwyg-lite.component.html',
  styleUrls: ['./wysiwyg-lite.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WysiwygLiteComponent),
      multi: true,
    },
  ],
})
export class WysiwygLiteComponent implements ControlValueAccessor, AfterViewInit {
  @Input() placeholder = 'Write here...';
  @Input() minHeight = 120;
  @Input() readOnly = false;
  @Input() disabled = false;
  @Output() blurred = new EventEmitter<void>();

  @ViewChild('root', { static: true })
  rootRef!: ElementRef<HTMLDivElement>;

  @ViewChild('editor', { static: true })
  editorRef!: ElementRef<HTMLDivElement>;

  value = '';
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    this.syncEditorHtml();
  }

  writeValue(value: string | null): void {
    const normalizedValue = normalizeRichTextHtml(value || '');
    this.value = normalizedValue;

    // When focused, avoid forcing innerHTML updates that reset caret position.
    if (this.isEditorFocused()) {
      return;
    }

    this.syncEditorHtml();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(): void {
    const currentHtml = this.editorRef.nativeElement.innerHTML;
    const normalized = sanitizeRichTextHtml(currentHtml);
    this.value = normalized;

    // Do not rewrite innerHTML while typing; it resets the caret and can invert typing order.
    if (!this.isEditorFocused() && this.editorRef.nativeElement.innerHTML !== normalized) {
      this.editorRef.nativeElement.innerHTML = normalized;
    }

    this.onChange(this.value);
  }

  onPaste(event: ClipboardEvent): void {
    if (this.isLocked) {
      return;
    }

    const clipboard = event.clipboardData;
    if (!clipboard) {
      return;
    }

    const html = clipboard.getData('text/html');
    const text = clipboard.getData('text/plain');
    const htmlContent = html ? normalizeRichTextHtml(html) : '';
    const contentToInsert = htmlContent || sectionedTextToRichTextHtml(text);

    if (!contentToInsert) {
      return;
    }

    event.preventDefault();
    this.insertHtmlAtCursor(contentToInsert);
    this.onInput();
  }

  onBlur(event: FocusEvent): void {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && this.rootRef?.nativeElement.contains(nextTarget)) {
      return;
    }

    // Normalize visible markup once editing is finished.
    this.syncEditorHtml();
    this.onTouched();
    this.blurred.emit();
  }

  onToolbarMouseDown(event: MouseEvent): void {
    if (!this.isSafariBrowser()) {
      return;
    }

    event.preventDefault();
  }

  exec(command: 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'insertOrderedList'): void {
    if (this.isLocked) {
      return;
    }

    this.editorRef.nativeElement.focus();
    document.execCommand(command, false);
    this.onInput();
  }

  clearFormatting(): void {
    if (this.isLocked) {
      return;
    }

    this.editorRef.nativeElement.focus();
    document.execCommand('removeFormat', false);
    this.onInput();
  }

  get isLocked(): boolean {
    return this.readOnly || this.disabled;
  }

  private syncEditorHtml(): void {
    if (!this.editorRef?.nativeElement) {
      return;
    }

    if (this.editorRef.nativeElement.innerHTML !== this.value) {
      this.editorRef.nativeElement.innerHTML = this.value;
    }
  }

  private insertHtmlAtCursor(html: string): void {
    this.editorRef.nativeElement.focus();

    if (document.execCommand('insertHTML', false, html)) {
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      this.editorRef.nativeElement.insertAdjacentHTML('beforeend', html);
      return;
    }

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const fragment = range.createContextualFragment(html);
    const lastChild = fragment.lastChild;
    range.insertNode(fragment);

    if (lastChild) {
      range.setStartAfter(lastChild);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  private isSafariBrowser(): boolean {
    const userAgent = navigator.userAgent;
    const isSafari = userAgent.includes('Safari');
    const isChrome = userAgent.includes('Chrome') || userAgent.includes('CriOS');
    const isChromiumEdge = userAgent.includes('Edg');
    const isFirefox = userAgent.includes('FxiOS');
    const isOpera = userAgent.includes('OPR') || userAgent.includes('OPiOS');

    return isSafari && !isChrome && !isChromiumEdge && !isFirefox && !isOpera;
  }

  private isEditorFocused(): boolean {
    return document.activeElement === this.editorRef?.nativeElement;
  }
}
